import React from "react";
import { useSelector, useDispatch } from 'react-redux'
import { FaShoppingCart } from 'react-icons/fa'
import { actions } from '../../store/actions'
import styles from '../../styles/add_to_cart.module.scss'
import { useTfaPassed } from '../../hooks/user'
import {requestSignInForThisPage} from "../../utils/url";
import {RootState} from "../../store/reducers/reducers";
import useCartAndQuotationItems from "../../hooks/use_cart_and_quotation_items";

interface AddToCartButtonProps {
  productId: string;
  quantity: number;
  variantPath?: string;
  maxQuantity: number;
  minQuantity: number;
  disabled: boolean;
  message: string;
  onSuccessMessage: (message: string) => void;
  onErrorMessage: (message: string) => void;
  showBuyNowButton?: boolean;
  isQuotationOnly?: boolean;
}

type AllowedButtonFactoryActions = typeof actions.cart.buyNow | typeof actions.cart.addItemAsync | typeof actions.quotationItem.addQuotationFromProduct;

function AddToCartButton({productId,
                           quantity,
                           variantPath,
                           maxQuantity,
                           minQuantity,
                           disabled,
                           message,
                           onSuccessMessage,
                           onErrorMessage,
                           showBuyNowButton = false,
                           isQuotationOnly}: AddToCartButtonProps) {
  const [locked, setLocked] = React.useState(false);
  const fetchedDataRef = React.useRef(false);

  const dispatch = useDispatch();
  const error = useSelector((state: RootState) => state.cart.addItemError);
  const isQuotationActionRef = React.useRef(false);
  const cartData = useCartAndQuotationItems();
  const cartItemCount = React.useMemo(() => {
    if (!cartData.isLoaded) {
      return 0;
    }

    return cartData.items.reduce((total, ci) => ci.item.quantity + total, 0);
  }, [cartData]);

  const tfaPassed = useTfaPassed();
  const lastCartItemCount = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (tfaPassed && !fetchedDataRef.current) {
      dispatch(actions.cart.fetchCart());
      dispatch(actions.quotationItem.fetchQuotationItems());
      fetchedDataRef.current = true;
    }
  }, [tfaPassed]);

  const buttonHandlerFactory = React.useCallback((dispatchAction: AllowedButtonFactoryActions) => async () => {
    if (tfaPassed) {
      if (minQuantity > quantity) {
        onErrorMessage('Must add at least quantity: ' + minQuantity);
        return;
      }

      if (maxQuantity > 0 && quantity > maxQuantity) {
        onErrorMessage('Can add at most quantity: ' + maxQuantity);
        return;
      }

      setLocked(true);
      lastCartItemCount.current = cartItemCount;
      isQuotationActionRef.current = dispatchAction === actions.quotationItem.addQuotationFromProduct;
      dispatch(dispatchAction(productId, variantPath, quantity, message));
    } else {
      await requestSignInForThisPage();
    }
  }, [quantity, minQuantity, maxQuantity, productId, variantPath, message, cartItemCount])

  const handleAddToCart = React.useCallback(buttonHandlerFactory(actions.cart.addItemAsync), [buttonHandlerFactory])
  const handleBuyNow = React.useCallback(buttonHandlerFactory(actions.cart.buyNow), [buttonHandlerFactory])
  const handleRequestQuotation = React.useCallback(buttonHandlerFactory(actions.quotationItem.addQuotationFromProduct), [buttonHandlerFactory])

  React.useEffect(() => {
    if (lastCartItemCount.current === null) {
      return
    }

    if (cartItemCount <= lastCartItemCount.current) {
      return
    }

    setLocked(false)

    if (isQuotationActionRef.current) {
      onSuccessMessage('Request received — check your cart for updates!');
    } else {
      onSuccessMessage('Added to cart');
    }
    
  }, [cartItemCount]);

  React.useEffect(() => {
    if (!error) {
      return
    }

    onErrorMessage(error)
    setLocked(false)
    lastCartItemCount.current = null
    dispatch(actions.cart.setAddItemError(null))
  }, [error]);


  return <>
    {(showBuyNowButton && !isQuotationOnly) && (
      <button onClick={handleBuyNow} className="btn btn-primary mr-2" disabled={disabled || locked}>
        Buy Now
      </button>
    )}

    {isQuotationOnly ? (
      <button onClick={handleRequestQuotation} disabled={disabled || locked} className="btn btn-primary">
        Request Quotation
      </button>
    ) : (
      <button onClick={handleAddToCart} className="btn btn-dark" disabled={disabled || locked}>
        <div className={styles.cartIcon}>
          <FaShoppingCart size={24} />
        </div>
      </button>
    )}
  </>
}

export default AddToCartButton
